import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient, { BASE_URL } from 'lib/api/apiClient';
import { SEARCH_URLS } from 'lib/api/endpoints';

// Build the request body, omitting any filter the user left at its "no filter"
// value. The backend treats absence as "no predicate," which lets old clients
// (that don't know about these fields) keep working unchanged.
// UI sort-field label → backend semantic name. `AOI` has no STAC
// equivalent (it's computed client-side from the drawn polygon) so it
// never ends up in the request body.
const SORT_FIELD_TO_BACKEND = {
  Date: 'datetime',
  Cloud: 'cloud_cover',
  Off_nadir: 'off_nadir',
  Sun: 'sun_elevation',
};

// Sent with every /api/search request. Mirrors the SortDropdown's default
// label so the backend doesn't silently fall back to each STAC API's
// undocumented default ordering.
export const DEFAULT_CATALOG_SORT = { field: 'Date', direction: 'desc' };

const toBackendSort = (cfg) => {
  if (!cfg || !cfg.field) return null;
  const field = SORT_FIELD_TO_BACKEND[cfg.field];
  if (!field) return null; // AOI — client-side only
  return { field, direction: cfg.direction === 'asc' ? 'asc' : 'desc' };
};

const backendSortsFromState = (sortByProduct) => {
  if (!sortByProduct) return null;
  const out = {};
  for (const [productId, cfg] of Object.entries(sortByProduct)) {
    const backendCfg = toBackendSort(cfg);
    if (backendCfg) out[productId] = backendCfg;
  }
  return Object.keys(out).length > 0 ? out : null;
};

// Replace the targeted products' items with `newItems`, keeping each product
// group in its ORIGINAL (first-seen) position. A plain filter+append would move
// a re-sorted product's items to the end of imageryData, which (for products
// without a defined group rank, e.g. Bhoonidhi) drops that group to the bottom.
const replaceProductItems = (existing, newItems, targetedPids) => {
  const targeted = new Set(targetedPids);
  const order = [];
  const byProduct = {};
  const bucket = (pid) => {
    if (!(pid in byProduct)) {
      byProduct[pid] = [];
      order.push(pid);
    }
    return byProduct[pid];
  };
  for (const it of existing) {
    const pid = it.product_id ?? '__nopid__';
    bucket(pid); // reserve the slot even for targeted products (items swapped below)
    if (!targeted.has(it.product_id)) byProduct[pid].push(it);
  }
  for (const it of newItems) {
    bucket(it.product_id ?? '__nopid__').push(it);
  }
  return order.flatMap((pid) => byProduct[pid]);
};

const buildSearchRequest = ({
  coordinates,
  dateRange,
  filters = {},
  cursors,
  sortByProduct,
}) => {
  const body = {
    coordinates: coordinates.map((coord) => ({ lat: coord.lat, lng: coord.lng })),
    dates: { startDate: dateRange.startDate, endDate: dateRange.endDate },
  };
  if (filters.maxCloudCover != null && filters.maxCloudCover < 100) {
    body.max_cloud_cover = filters.maxCloudCover;
  }
  if (filters.minSunElevation != null && filters.minSunElevation > 0) {
    body.min_sun_elevation = filters.minSunElevation;
  }
  if (filters.maxOffNadirAngle != null && filters.maxOffNadirAngle < 90) {
    body.max_off_nadir = filters.maxOffNadirAngle;
  }
  if (filters.minResolution != null && filters.minResolution > 0) {
    body.min_resolution = filters.minResolution;
  }
  if (filters.maxResolution != null) {
    body.max_resolution = filters.maxResolution;
  }
  // Non-empty cursors = "only query these products, at these offsets."
  // Absent/empty = fresh search across every accessible product.
  if (cursors && Object.keys(cursors).length > 0) {
    body.cursors = cursors;
  }
  const sorts = backendSortsFromState(sortByProduct);
  if (sorts) {
    body.sorts = sorts;
  }
  // No `default_sort` on the wire — the backend falls back to `datetime
  // desc` for any product without an explicit entry in `sorts`. The
  // `DEFAULT_CATALOG_SORT` constant is still used by the SortDropdown
  // display and by toggleSort's first-click fallback.
  return body;
};

// Fresh search. Replaces imageryData + resets cursors/totals from the response.
export const searchCatalog = createAsyncThunk(
  'catalog/searchCatalog',
  async ({ coordinates, dateRange, filters }, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const sortByProduct = state.catalog.catalogSortByProduct;
      const requestBody = buildSearchRequest({
        coordinates,
        dateRange,
        filters,
        sortByProduct,
      });
      const response = await apiClient.post(SEARCH_URLS.catalog, requestBody, {
        signal: thunkAPI.signal,
      });
      const data = response.data;
      const payload = data?.data ?? data;
      return {
        items: payload?.items || [],
        cursors: payload?.cursors || {},
        totals: payload?.totals || {},
        hasMore: Boolean(payload?.has_more),
      };
    } catch (error) {
      if (error.response?.status === 504) {
        return thunkAPI.rejectWithValue('AOI too large. Try with a smaller AOI.');
      }
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Catalog search failed');
    }
  }
);

// Refetch page 0 for a specific set of product_ids (e.g. after a sort
// change for one satellite group). Items for OTHER products stay
// untouched in the store; only the targeted products are replaced.
export const refetchProductsCatalog = createAsyncThunk(
  'catalog/refetchProductsCatalog',
  async ({ productIds }, thunkAPI) => {
    const state = thunkAPI.getState();
    const coordinates = state.aoi.drawnCoordinates;
    const dateRange = state.catalog.catalogDateRange;
    const filters = state.catalog.catalogFilters;
    const sortByProduct = state.catalog.catalogSortByProduct;

    if (!coordinates?.length || !dateRange?.startDate) {
      return { items: [], cursors: {}, totals: {}, productIds };
    }

    // Non-empty cursors with offset 0 tells the backend: "only query
    // these products, starting from the beginning."
    const cursors = Object.fromEntries(productIds.map((pid) => [pid, 0]));

    try {
      const requestBody = buildSearchRequest({
        coordinates,
        dateRange,
        filters,
        cursors,
        sortByProduct,
      });
      const response = await apiClient.post(SEARCH_URLS.catalog, requestBody, {
        signal: thunkAPI.signal,
      });
      const data = response.data;
      const payload = data?.data ?? data;
      return {
        items: payload?.items || [],
        cursors: payload?.cursors || {},
        totals: payload?.totals || {},
        productIds,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || 'Refetch failed'
      );
    }
  }
);

// Fetch the next page for a specific set of product_ids (Load More on
// one satellite group). The response items are APPENDED to imageryData.
export const loadMoreCatalog = createAsyncThunk(
  'catalog/loadMoreCatalog',
  async ({ productIds }, thunkAPI) => {
    const state = thunkAPI.getState();
    const coordinates = state.aoi.drawnCoordinates;
    const dateRange = state.catalog.catalogDateRange;
    const filters = state.catalog.catalogFilters;
    const sortByProduct = state.catalog.catalogSortByProduct;
    const allCursors = state.catalog.catalogCursors || {};

    const cursors = {};
    for (const pid of productIds) {
      if (allCursors[pid] != null) cursors[pid] = allCursors[pid];
    }
    if (Object.keys(cursors).length === 0) {
      return { items: [], cursors: {}, totals: {}, advancedProductIds: productIds };
    }

    try {
      const requestBody = buildSearchRequest({
        coordinates,
        dateRange,
        filters,
        cursors,
        sortByProduct,
      });
      const response = await apiClient.post(SEARCH_URLS.catalog, requestBody, {
        signal: thunkAPI.signal,
      });
      const data = response.data;
      const payload = data?.data ?? data;
      return {
        items: payload?.items || [],
        cursors: payload?.cursors || {},
        totals: payload?.totals || {},
        advancedProductIds: productIds,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || 'Load more failed'
      );
    }
  }
);

// Read-only explore search against ISRO Bhoonidhi (footprints + quicklook
// previews). The coordinator fires this instead of searchCatalog when
// `catalogSource === 'bhoonidhi'` (source is set by the Bhoonidhi role). Its
// results REPLACE imageryData; pagination mirrors searchCatalog (per-collection
// integer cursors keyed by product_id).
// Internal STAC "My Images" search (role-gated, fires alongside the normal
// catalog search with the same AOI + dates; results live in their own state
// so the Catalog tab is untouched). The backend returns items whose
// preview_url and assets.tiles.href are RELATIVE proxy paths (it can't know
// its public host behind proxies) — absolutize them here against the API base
// so <img> tags and Leaflet tile layers load from the right origin.
export const fetchCreditUsage = createAsyncThunk(
  'catalog/fetchCreditUsage',
  async (_, thunkAPI) => {
    try {
      const res = await apiClient.get(SEARCH_URLS.myImagesUsage);
      return res.data?.data ?? res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'usage fetch failed');
    }
  }
);

export const fetchDownloadQuote = createAsyncThunk(
  'catalog/fetchDownloadQuote',
  async (itemIds, thunkAPI) => {
    try {
      const res = await apiClient.post(SEARCH_URLS.myImagesQuote, { item_ids: itemIds });
      return res.data?.data ?? res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'quote failed');
    }
  }
);

export const startDownload = createAsyncThunk(
  'catalog/startDownload',
  async (itemIds, thunkAPI) => {
    try {
      const res = await apiClient.post(SEARCH_URLS.myImagesDownload, { item_ids: itemIds });
      return res.data?.data ?? res.data;
    } catch (error) {
      // 402 detail is a structured object { message, need, have, resets_at, admin_contact }
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Download failed');
    }
  }
);

export const searchMyImages = createAsyncThunk(
  'catalog/searchMyImages',
  async (arg, thunkAPI) => {
    const state = thunkAPI.getState();
    const coordinates = arg?.coordinates ?? state.aoi.drawnCoordinates;
    const dateRange = arg?.dateRange ?? state.catalog.catalogDateRange;
    if (!coordinates?.length || !dateRange?.startDate) {
      return thunkAPI.rejectWithValue('Draw an AOI and pick a date range first.');
    }
    try {
      const filters = state.catalog.myImagesFilters ?? arg?.filters ?? {};
      const requestBody = {
        coordinates: coordinates.map((coord) => ({ lat: coord.lat, lng: coord.lng })),
        dates: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      };
      if (filters.maxCloudCover != null && filters.maxCloudCover < 100) {
        requestBody.max_cloud_cover = filters.maxCloudCover;
      }
      if (filters.minSunElevation != null && filters.minSunElevation > 0) {
        requestBody.min_sun_elevation = filters.minSunElevation;
      }
      if (filters.maxOffNadirAngle != null && filters.maxOffNadirAngle < 90) {
        requestBody.max_off_nadir = filters.maxOffNadirAngle;
      }
      if (filters.minResolution != null && filters.minResolution > 0) {
        requestBody.min_resolution = filters.minResolution;
      }
      if (filters.maxResolution != null) {
        requestBody.max_resolution = filters.maxResolution;
      }
      const response = await apiClient.post(SEARCH_URLS.myImages, requestBody, {
        signal: thunkAPI.signal,
      });
      const payload = response.data?.data ?? response.data;
      const absolutize = (path) => (path && path.startsWith('/') ? `${BASE_URL}${path}` : path);
      const items = (payload?.items || []).map((item) => ({
        ...item,
        preview_url: absolutize(item.preview_url),
        assets: item.assets?.tiles?.href
          ? {
              ...item.assets,
              tiles: { ...item.assets.tiles, href: absolutize(item.assets.tiles.href) },
            }
          : item.assets,
      }));
      return { items, totals: payload?.totals || {} };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || 'My Images search failed'
      );
    }
  }
);

// Whole internal archive (no AOI/date) for the "SatSure High Resolution"
// product page. Same item shape as searchMyImages (relative proxy hrefs
// absolutized here), plus a numeric `size` per item.
export const fetchProcuredImages = createAsyncThunk(
  'catalog/fetchProcuredImages',
  async (_arg, thunkAPI) => {
    try {
      const response = await apiClient.get(SEARCH_URLS.myImagesAll, {
        signal: thunkAPI.signal,
      });
      const payload = response.data?.data ?? response.data;
      const absolutize = (path) => (path && path.startsWith('/') ? `${BASE_URL}${path}` : path);
      const items = (payload?.items || []).map((item) => ({
        ...item,
        preview_url: absolutize(item.preview_url),
        assets: item.assets?.tiles?.href
          ? {
              ...item.assets,
              tiles: { ...item.assets.tiles, href: absolutize(item.assets.tiles.href) },
            }
          : item.assets,
      }));
      return { items, totals: payload?.totals || {} };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || 'Failed to load procured images'
      );
    }
  }
);

export const searchBhoonidhiExplore = createAsyncThunk(
  'catalog/searchBhoonidhiExplore',
  async (arg, thunkAPI) => {
    const state = thunkAPI.getState();
    const coordinates = arg?.coordinates ?? state.aoi.drawnCoordinates;
    const dateRange = arg?.dateRange ?? state.catalog.catalogDateRange;
    if (!coordinates?.length || !dateRange?.startDate) {
      return thunkAPI.rejectWithValue('Draw an AOI and pick a date range first.');
    }
    try {
      const sorts = backendSortsFromState(state.catalog.catalogSortByProduct);
      const requestBody = {
        coordinates: coordinates.map((coord) => ({ lat: coord.lat, lng: coord.lng })),
        dates: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      };
      if (sorts) requestBody.sorts = sorts;
      const response = await apiClient.post(SEARCH_URLS.bhoonidhiExplore, requestBody, {
        signal: thunkAPI.signal,
      });
      const payload = response.data?.data ?? response.data;
      return {
        items: payload?.items || [],
        cursors: payload?.cursors || {},
        totals: payload?.totals || {},
        hasMore: Boolean(payload?.has_more),
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || 'Bhoonidhi search failed'
      );
    }
  }
);

// Stage the currently selected Bhoonidhi scenes into the portal cart. Fired on
// the trimmed checkout dialog's "Confirm Selection - Redirect to Bhoonidhi".
// Each selected scene carries its raw ProductSearch record on
// `extra.bhoonidhi_raw` (the explore endpoint stashes it there); we post those
// verbatim to the role-gated endpoint, which adds them to the user's own cart
// (creds set in Settings). The backend reuses the cached session and only
// returns `otp_required` when it lapses. Returns
// { status: 'added'|'otp_required', added, failed, portalUrl, pendingToken }.
// The dialog owns its own submit/error state, so this thunk needs no reducers.
const _selectedRawScenes = (state) =>
  (state.catalog.selectedImages || [])
    .map((img) => img?.extra?.bhoonidhi_raw)
    .filter(Boolean);

export const addToBhoonidhiCart = createAsyncThunk(
  'catalog/addToBhoonidhiCart',
  async (_, thunkAPI) => {
    const scenes = _selectedRawScenes(thunkAPI.getState());
    if (scenes.length === 0) {
      return thunkAPI.rejectWithValue({
        message:
          'Selected scenes are missing Bhoonidhi data. Re-run the search and try again.',
      });
    }
    try {
      const response = await apiClient.post(SEARCH_URLS.bhoonidhiCartAdd, { scenes });
      const payload = response.data?.data ?? response.data;
      return {
        status: payload?.status, // 'added' | 'otp_required'
        added: payload?.added || [],
        failed: payload?.failed || [],
        portalUrl: payload?.portal_url || null,
        pendingToken: payload?.pending_token || null,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message:
          error.response?.data?.detail ||
          'Failed to add scenes to the Bhoonidhi cart.',
      });
    }
  }
);

// Completes an add-to-cart OTP challenge (when the cached session had lapsed):
// re-sends the same scenes with the mailed code. Returns { added, failed, portalUrl }.
export const verifyBhoonidhiCartOtp = createAsyncThunk(
  'catalog/verifyBhoonidhiCartOtp',
  async ({ pendingToken, otp }, thunkAPI) => {
    const scenes = _selectedRawScenes(thunkAPI.getState());
    try {
      const response = await apiClient.post(SEARCH_URLS.bhoonidhiCartVerifyOtp, {
        pending_token: pendingToken,
        otp,
        scenes,
      });
      const payload = response.data?.data ?? response.data;
      return {
        status: payload?.status,
        added: payload?.added || [],
        failed: payload?.failed || [],
        portalUrl: payload?.portal_url || null,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.response?.data?.detail || 'OTP verification failed.',
      });
    }
  }
);

// Load More for a Bhoonidhi satellite group — fetches the next page for the
// given collection(s) using their current cursors and APPENDS the results.
export const loadMoreBhoonidhi = createAsyncThunk(
  'catalog/loadMoreBhoonidhi',
  async ({ productIds }, thunkAPI) => {
    const state = thunkAPI.getState();
    const coordinates = state.aoi.drawnCoordinates;
    const dateRange = state.catalog.catalogDateRange;
    const allCursors = state.catalog.catalogCursors || {};

    const cursors = {};
    for (const pid of productIds) {
      if (allCursors[pid] != null) cursors[pid] = allCursors[pid];
    }
    if (Object.keys(cursors).length === 0) {
      return { items: [], cursors: {}, totals: {}, advancedProductIds: productIds };
    }
    try {
      const sorts = backendSortsFromState(state.catalog.catalogSortByProduct);
      const requestBody = {
        coordinates: coordinates.map((coord) => ({ lat: coord.lat, lng: coord.lng })),
        dates: { startDate: dateRange.startDate, endDate: dateRange.endDate },
        cursors,
      };
      if (sorts) requestBody.sorts = sorts;
      const response = await apiClient.post(SEARCH_URLS.bhoonidhiExplore, requestBody, {
        signal: thunkAPI.signal,
      });
      const payload = response.data?.data ?? response.data;
      return {
        items: payload?.items || [],
        cursors: payload?.cursors || {},
        totals: payload?.totals || {},
        advancedProductIds: productIds,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Load more failed');
    }
  }
);

// Refetch page 0 for specific Bhoonidhi collection(s) after a sort change — the
// Bhoonidhi twin of refetchProductsCatalog. Sends cursors at offset 0 (so only
// those collections are queried) plus the current sorts; the backend date-sorts
// each servlet page before paginating. Results REPLACE those products' items;
// the affected group shows the sort spinner while others stay put.
export const refetchProductsBhoonidhi = createAsyncThunk(
  'catalog/refetchProductsBhoonidhi',
  async ({ productIds }, thunkAPI) => {
    const state = thunkAPI.getState();
    const coordinates = state.aoi.drawnCoordinates;
    const dateRange = state.catalog.catalogDateRange;
    if (!coordinates?.length || !dateRange?.startDate) {
      return { items: [], cursors: {}, totals: {}, productIds };
    }
    // Non-empty cursors with offset 0 → "only these collections, from the start."
    const cursors = Object.fromEntries(productIds.map((pid) => [pid, 0]));
    const sorts = backendSortsFromState(state.catalog.catalogSortByProduct);
    try {
      const requestBody = {
        coordinates: coordinates.map((coord) => ({ lat: coord.lat, lng: coord.lng })),
        dates: { startDate: dateRange.startDate, endDate: dateRange.endDate },
        cursors,
      };
      if (sorts) requestBody.sorts = sorts;
      const response = await apiClient.post(SEARCH_URLS.bhoonidhiExplore, requestBody, {
        signal: thunkAPI.signal,
      });
      const payload = response.data?.data ?? response.data;
      return {
        items: payload?.items || [],
        cursors: payload?.cursors || {},
        totals: payload?.totals || {},
        productIds,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Refetch failed');
    }
  }
);

// "Select all" for a Bhoonidhi product: pull the WHOLE product in one request
// (page_size override → the backend returns every page, sorted) and select all
// of it. Unlike Load More, this drops the product's cursor (so "Show More"
// disappears) and marks every returned scene selected. Selection is set in the
// reducer directly (not via per-image map layers) so hundreds of scenes don't
// spam the map with polygons / fitBounds.
export const selectAllProductBhoonidhi = createAsyncThunk(
  'catalog/selectAllProductBhoonidhi',
  async ({ productIds }, thunkAPI) => {
    const state = thunkAPI.getState();
    const coordinates = state.aoi.drawnCoordinates;
    const dateRange = state.catalog.catalogDateRange;
    if (!coordinates?.length || !dateRange?.startDate) {
      return { items: [], cursors: {}, totals: {}, productIds };
    }
    const cursors = Object.fromEntries(productIds.map((pid) => [pid, 0]));
    const sorts = backendSortsFromState(state.catalog.catalogSortByProduct);
    try {
      const requestBody = {
        coordinates: coordinates.map((coord) => ({ lat: coord.lat, lng: coord.lng })),
        dates: { startDate: dateRange.startDate, endDate: dateRange.endDate },
        cursors,
        page_size: 10000, // one shot: the whole product (backend fetch-caps at 10k)
      };
      if (sorts) requestBody.sorts = sorts;
      const response = await apiClient.post(SEARCH_URLS.bhoonidhiExplore, requestBody, {
        signal: thunkAPI.signal,
      });
      const payload = response.data?.data ?? response.data;
      return {
        items: payload?.items || [],
        cursors: payload?.cursors || {},
        totals: payload?.totals || {},
        productIds,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Select all failed');
    }
  }
);

const catalogSlice = createSlice({
  name: 'catalog',
  initialState: {
    imageryData: [],
    catalogDateRange: null,
    taskingDateRange: null,
    // `null` for any key = no filter / use backend default.
    catalogFilters: {
      maxCloudCover: null,
      minSunElevation: null,
      maxOffNadirAngle: null,
      minResolution: null,
      maxResolution: null,
    },
    // My Images has its OWN committed filters so the tab remembers them
    // independently of the catalog tab (same key shape as catalogFilters).
    myImagesFilters: {
      maxCloudCover: null,
      minSunElevation: null,
      maxOffNadirAngle: null,
      minResolution: null,
      maxResolution: null,
    },
    // Per-product pagination cursors returned by the last response.
    // Empty when no product has more items to fetch.
    catalogCursors: {},
    hasMore: false,
    // Per-product total match counts (from STAC's `matched`) — powers
    // the "X/N selected" chip so the denominator is the real total for
    // the search, not whatever we happened to have loaded.
    totalsByProduct: {},
    // Per-product loading flags so satellite groups can show a spinner
    // in exactly the right place (inside the grid for sort-refetch, in
    // place of the Load More button for load-more).
    sortingProductIds: [],
    loadingMoreProductIds: [],
    // Products currently running a bulk "Select all" fetch (spinner on the box).
    selectingAllProductIds: [],
    // Per-product sort: `{ [product_id]: { field: 'Date'|'Cloud'|'Off_nadir'|'Sun'|'AOI', direction: 'asc'|'desc' } }`
    // Written by the SatelliteGroup sort dropdown; read by the search
    // coordinator (change → refetch) and by the thunks (serialized to the
    // request body, except `AOI` which stays client-side).
    catalogSortByProduct: {},
    selectedImages: [],
    visibleImage: null,
    hoveredImage: null,
    satelliteVisibility: {},
    searchLoading: false,
    searchError: null,
    // Internal STAC "My Images" results — separate from imageryData so the
    // Catalog tab's list is never touched. Selection/visibility state is
    // SHARED with the catalog (same selectedImages et al.) so the map layer
    // machinery works unchanged for both tabs.
    procuredImages: [],       // whole internal archive for the SatSure High Resolution page
    procuredLoading: false,
    procuredError: null,
    myImagesData: [],
    myImagesTotals: {},
    myImagesLoading: false,
    myImagesError: null,
    // My Images credits
    creditUsage: null,        // { unit, grant, used, remaining, resets_at, admin_contact }
    creditsVersion: 0,        // bump to force CreditsChip to refetch after a download
    downloadQuote: null,      // { items:[{item_id,credits}], total_credits }
    quoteLoading: false,      // the credit quote is in flight (separate from the download itself)
    downloadLoading: false,
    downloadError: null,      // { message, need, have, resets_at, admin_contact } on 402
    // Active catalog data source: 'catalog' (default aggregated providers) or
    // 'bhoonidhi' (read-only ISRO Bhoonidhi explore). Set by the Source dropdown.
    catalogSource: 'catalog',
  },
  reducers: {
    setCatalogDateRange(state, action) {
      state.catalogDateRange = action.payload;
    },
    setCatalogFilter(state, action) {
      const { key, value } = action.payload;
      state.catalogFilters[key] = value;
    },
    setMyImagesFilter(state, action) {
      const { key, value } = action.payload;
      state.myImagesFilters[key] = value;
    },
    setCatalogSortForProduct(state, action) {
      const { productIds, field, direction } = action.payload;
      for (const pid of productIds) {
        state.catalogSortByProduct[pid] = { field, direction };
      }
    },
    clearCatalogSort(state) {
      state.catalogSortByProduct = {};
    },
    setTaskingDateRange(state, action) {
      state.taskingDateRange = action.payload;
    },
    setSelectedImages(state, action) {
      state.selectedImages = action.payload;
    },
    toggleImageSelection(state, action) {
      const image = action.payload;
      const idx = state.selectedImages.findIndex((i) => i.id === image.id);
      if (idx >= 0) {
        state.selectedImages.splice(idx, 1);
      } else {
        state.selectedImages.push(image);
      }
    },
    setVisibleImage(state, action) {
      state.visibleImage = action.payload;
    },
    setHoveredImage(state, action) {
      state.hoveredImage = action.payload;
    },
    setSatelliteVisibility(state, action) {
      state.satelliteVisibility = action.payload;
    },
    toggleSatelliteVisibility(state, action) {
      const satellite = action.payload;
      state.satelliteVisibility[satellite] = !state.satelliteVisibility[satellite];
    },
    setImageryData(state, action) {
      state.imageryData = action.payload;
    },
    setSearchLoading(state, action) {
      state.searchLoading = action.payload;
    },
    clearCatalogResults(state) {
      state.imageryData = [];
      state.selectedImages = [];
      state.visibleImage = null;
      state.hoveredImage = null;
      state.searchError = null;
      state.catalogCursors = {};
      state.totalsByProduct = {};
      state.hasMore = false;
    },
    // AOI exceeds the max area: clear results + surface a message (no search).
    aoiTooLarge(state, action) {
      state.imageryData = [];
      state.selectedImages = [];
      state.visibleImage = null;
      state.hoveredImage = null;
      state.catalogCursors = {};
      state.totalsByProduct = {};
      state.hasMore = false;
      state.searchLoading = false;
      state.searchError = action.payload;
    },
    // Switch the catalog data source. The search coordinator re-fires the
    // matching search (catalog vs Bhoonidhi) when this changes.
    setCatalogSource(state, action) {
      state.catalogSource = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreditUsage.fulfilled, (state, action) => {
        state.creditUsage = action.payload;
      })
      .addCase(fetchDownloadQuote.pending, (state) => {
        state.quoteLoading = true;
        state.downloadQuote = null;
        state.downloadError = null;
      })
      .addCase(fetchDownloadQuote.fulfilled, (state, action) => {
        state.quoteLoading = false;
        state.downloadQuote = action.payload;
      })
      .addCase(fetchDownloadQuote.rejected, (state, action) => {
        // Previously unhandled: a failed quote left the modal waiting forever.
        state.quoteLoading = false;
        state.downloadError = action.payload;
      })
      .addCase(startDownload.pending, (state) => {
        state.downloadLoading = true;
        state.downloadError = null;
      })
      .addCase(startDownload.fulfilled, (state, action) => {
        state.downloadLoading = false;
        // Patch the balance straight from the server's response (it recomputed
        // it under a lock) so the footer is exact immediately; the version bump
        // also triggers a refetch to reconcile any other-session spend.
        if (state.creditUsage && action.payload) {
          const charged = action.payload.charged_credits ?? 0;
          state.creditUsage = {
            ...state.creditUsage,
            used: Math.round(((state.creditUsage.used ?? 0) + charged) * 100) / 100,
            remaining:
              action.payload.remaining_credits != null
                ? action.payload.remaining_credits
                : Math.max(0, (state.creditUsage.remaining ?? 0) - charged),
          };
        }
        state.creditsVersion += 1;
      })
      .addCase(startDownload.rejected, (state, action) => {
        state.downloadLoading = false;
        state.downloadError = action.payload;
      })
      .addCase(searchCatalog.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
        // Clear the previous catalog scenes off the map the moment a new search
        // starts (keep My Images selections — searchMyImages.pending clears those).
        state.selectedImages = state.selectedImages.filter((img) => img._source === 'myimages');
        state.visibleImage = null;
        state.hoveredImage = null;
      })
      .addCase(searchCatalog.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.imageryData = action.payload.items;
        state.catalogCursors = action.payload.cursors;
        state.totalsByProduct = action.payload.totals;
        state.hasMore = action.payload.hasMore;
        // Clear stale catalog selections, but keep My Images ones so a catalog
        // search doesn't wipe the internal-imagery footprints on the map.
        state.selectedImages = state.selectedImages.filter((img) => img._source === 'myimages');
        state.visibleImage = null;
        state.hoveredImage = null;
        state.satelliteVisibility = {};
      })
      .addCase(searchCatalog.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.searchLoading = false;
        state.searchError = action.payload;
      })
      .addCase(loadMoreCatalog.pending, (state, action) => {
        const pids = action.meta.arg?.productIds || [];
        state.loadingMoreProductIds = Array.from(
          new Set([...state.loadingMoreProductIds, ...pids])
        );
      })
      .addCase(loadMoreCatalog.fulfilled, (state, action) => {
        const { items, cursors, totals, advancedProductIds } = action.payload;
        state.loadingMoreProductIds = state.loadingMoreProductIds.filter(
          (p) => !advancedProductIds.includes(p)
        );
        // Append the new page to imageryData.
        state.imageryData = [...state.imageryData, ...items];
        // Update cursors for the advanced products (missing = exhausted).
        const nextCursors = { ...state.catalogCursors };
        for (const pid of advancedProductIds) {
          if (cursors[pid] != null) nextCursors[pid] = cursors[pid];
          else delete nextCursors[pid];
        }
        state.catalogCursors = nextCursors;
        state.hasMore = Object.keys(nextCursors).length > 0;
        // Totals shouldn't change across pages of the same search, but
        // refresh anyway in case the backend re-reports.
        state.totalsByProduct = { ...state.totalsByProduct, ...totals };
      })
      .addCase(loadMoreCatalog.rejected, (state, action) => {
        const pids = action.meta.arg?.productIds || [];
        state.loadingMoreProductIds = state.loadingMoreProductIds.filter(
          (p) => !pids.includes(p)
        );
        if (action.meta.aborted) return;
        state.searchError = action.payload;
      })
      .addCase(refetchProductsCatalog.pending, (state, action) => {
        const pids = action.meta.arg?.productIds || [];
        state.sortingProductIds = Array.from(
          new Set([...state.sortingProductIds, ...pids])
        );
      })
      .addCase(refetchProductsCatalog.fulfilled, (state, action) => {
        const { items, cursors, totals, productIds } = action.payload;
        state.sortingProductIds = state.sortingProductIds.filter(
          (p) => !productIds.includes(p)
        );
        // Swap in the refetched products' items in place (keeps their group
        // position; a filter+append would bump the group to the bottom).
        state.imageryData = replaceProductItems(state.imageryData, items, productIds);
        // Reset cursors + totals for the refetched products.
        const nextCursors = { ...state.catalogCursors };
        const nextTotals = { ...state.totalsByProduct };
        for (const pid of productIds) {
          if (cursors[pid] != null) nextCursors[pid] = cursors[pid];
          else delete nextCursors[pid];
          if (totals[pid] != null) nextTotals[pid] = totals[pid];
        }
        state.catalogCursors = nextCursors;
        state.totalsByProduct = nextTotals;
        state.hasMore = Object.keys(nextCursors).length > 0;
        // Intentionally DO NOT clear selectedImages / visibleImage /
        // hoveredImage — selections persist across re-sorts so when the
        // selected card reappears (via another sort or Load More) its
        // "selected" state comes back with it.
      })
      .addCase(refetchProductsCatalog.rejected, (state, action) => {
        const pids = action.meta.arg?.productIds || [];
        state.sortingProductIds = state.sortingProductIds.filter(
          (p) => !pids.includes(p)
        );
        if (action.meta.aborted) return;
        state.searchError = action.payload;
      })
      // Bhoonidhi per-product re-sort — mirrors refetchProductsCatalog: spinner
      // on the targeted product(s), then replace their items with the sorted
      // page 0; everything else stays put.
      .addCase(refetchProductsBhoonidhi.pending, (state, action) => {
        const pids = action.meta.arg?.productIds || [];
        state.sortingProductIds = Array.from(
          new Set([...state.sortingProductIds, ...pids])
        );
      })
      .addCase(refetchProductsBhoonidhi.fulfilled, (state, action) => {
        const { items, cursors, totals, productIds } = action.payload;
        state.sortingProductIds = state.sortingProductIds.filter(
          (p) => !productIds.includes(p)
        );
        // Swap in place — keep the product's group where it was.
        state.imageryData = replaceProductItems(state.imageryData, items, productIds);
        const nextCursors = { ...state.catalogCursors };
        const nextTotals = { ...state.totalsByProduct };
        for (const pid of productIds) {
          if (cursors[pid] != null) nextCursors[pid] = cursors[pid];
          else delete nextCursors[pid];
          if (totals[pid] != null) nextTotals[pid] = totals[pid];
        }
        state.catalogCursors = nextCursors;
        state.totalsByProduct = nextTotals;
        state.hasMore = Object.keys(nextCursors).length > 0;
      })
      .addCase(refetchProductsBhoonidhi.rejected, (state, action) => {
        const pids = action.meta.arg?.productIds || [];
        state.sortingProductIds = state.sortingProductIds.filter(
          (p) => !pids.includes(p)
        );
        if (action.meta.aborted) return;
        state.searchError = action.payload;
      })
      // Bhoonidhi "Select all" — load the whole product, drop its cursor (so
      // "Show More" goes away), and select every returned scene.
      .addCase(selectAllProductBhoonidhi.pending, (state, action) => {
        const pids = action.meta.arg?.productIds || [];
        state.selectingAllProductIds = Array.from(
          new Set([...state.selectingAllProductIds, ...pids])
        );
      })
      .addCase(selectAllProductBhoonidhi.fulfilled, (state, action) => {
        const { items, cursors, totals, productIds } = action.payload;
        state.selectingAllProductIds = state.selectingAllProductIds.filter(
          (p) => !productIds.includes(p)
        );
        // Render every fetched scene in place of the product's current page.
        state.imageryData = replaceProductItems(state.imageryData, items, productIds);
        // Drop cursors for these products → "Show More" disappears (all loaded).
        const nextCursors = { ...state.catalogCursors };
        const nextTotals = { ...state.totalsByProduct };
        for (const pid of productIds) {
          if (cursors[pid] != null) nextCursors[pid] = cursors[pid];
          else delete nextCursors[pid];
          if (totals[pid] != null) nextTotals[pid] = totals[pid];
        }
        state.catalogCursors = nextCursors;
        state.totalsByProduct = nextTotals;
        state.hasMore = Object.keys(nextCursors).length > 0;
        // Selection itself is applied in the component (via onImageSelect, with
        // the display-shaped item + footprint-only render), not here.
      })
      .addCase(selectAllProductBhoonidhi.rejected, (state, action) => {
        const pids = action.meta.arg?.productIds || [];
        state.selectingAllProductIds = state.selectingAllProductIds.filter(
          (p) => !pids.includes(p)
        );
        if (action.meta.aborted) return;
        state.searchError = action.payload;
      })
      .addCase(searchBhoonidhiExplore.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
        // Same as searchCatalog.pending — clear old scenes off the map at search
        // start (My Images selections handled by searchMyImages.pending).
        state.selectedImages = state.selectedImages.filter((img) => img._source === 'myimages');
        state.visibleImage = null;
        state.hoveredImage = null;
      })
      .addCase(searchBhoonidhiExplore.fulfilled, (state, action) => {
        state.searchLoading = false;
        // Fresh Bhoonidhi search — replace the list and reset cursors/totals
        // from the response (same shape as searchCatalog.fulfilled).
        state.imageryData = action.payload.items;
        state.catalogCursors = action.payload.cursors;
        state.totalsByProduct = action.payload.totals;
        state.hasMore = action.payload.hasMore;
        state.selectedImages = state.selectedImages.filter((img) => img._source === 'myimages');
        state.visibleImage = null;
        state.hoveredImage = null;
        state.satelliteVisibility = {};
      })
      .addCase(searchBhoonidhiExplore.rejected, (state, action) => {
        // Aborted = superseded by a newer search (rapid re-fires on landing as
        // filters/date settle). Return BEFORE touching searchLoading so the
        // newer search's pending:true isn't clobbered — otherwise the loader
        // vanishes and "No Imagery Found" flashes until results arrive.
        if (action.meta.aborted) return;
        state.searchLoading = false;
        state.searchError = action.payload;
      })
      .addCase(searchMyImages.pending, (state) => {
        state.myImagesLoading = true;
        state.myImagesError = null;
        // Clear the previous internal scenes off the map when a new My Images
        // search starts (catalog/Bhoonidhi selections handled by their pending).
        state.selectedImages = state.selectedImages.filter((img) => img._source !== 'myimages');
      })
      .addCase(searchMyImages.fulfilled, (state, action) => {
        state.myImagesLoading = false;
        state.myImagesData = action.payload.items;
        state.myImagesTotals = action.payload.totals;
        // Drop My Images selections that aren't in the new results; keep catalog
        // selections and any My Images scene still present.
        const ids = new Set((action.payload.items || []).map((i) => i.id));
        state.selectedImages = state.selectedImages.filter(
          (img) => img._source !== 'myimages' || ids.has(img.id)
        );
      })
      .addCase(searchMyImages.rejected, (state, action) => {
        // Aborted-first, same reasoning as searchBhoonidhiExplore.rejected.
        if (action.meta.aborted) return;
        state.myImagesLoading = false;
        state.myImagesError = action.payload;
      })
      .addCase(fetchProcuredImages.pending, (state) => {
        state.procuredLoading = true;
        state.procuredError = null;
      })
      .addCase(fetchProcuredImages.fulfilled, (state, action) => {
        state.procuredLoading = false;
        state.procuredImages = action.payload.items;
      })
      .addCase(fetchProcuredImages.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.procuredLoading = false;
        state.procuredError = action.payload;
      })
      .addCase(loadMoreBhoonidhi.pending, (state, action) => {
        const pids = action.meta.arg?.productIds || [];
        state.loadingMoreProductIds = Array.from(
          new Set([...state.loadingMoreProductIds, ...pids])
        );
      })
      .addCase(loadMoreBhoonidhi.fulfilled, (state, action) => {
        const { items, cursors, totals, advancedProductIds } = action.payload;
        state.loadingMoreProductIds = state.loadingMoreProductIds.filter(
          (p) => !advancedProductIds.includes(p)
        );
        state.imageryData = [...state.imageryData, ...items];
        const nextCursors = { ...state.catalogCursors };
        for (const pid of advancedProductIds) {
          if (cursors[pid] != null) nextCursors[pid] = cursors[pid];
          else delete nextCursors[pid];
        }
        state.catalogCursors = nextCursors;
        state.hasMore = Object.keys(nextCursors).length > 0;
        state.totalsByProduct = { ...state.totalsByProduct, ...totals };
      })
      .addCase(loadMoreBhoonidhi.rejected, (state, action) => {
        const pids = action.meta.arg?.productIds || [];
        state.loadingMoreProductIds = state.loadingMoreProductIds.filter(
          (p) => !pids.includes(p)
        );
        if (action.meta.aborted) return;
        state.searchError = action.payload;
      });
  },
});

export const {
  setCatalogDateRange,
  setCatalogFilter,
  setMyImagesFilter,
  setCatalogSortForProduct,
  clearCatalogSort,
  setTaskingDateRange,
  setSelectedImages,
  toggleImageSelection,
  setVisibleImage,
  setHoveredImage,
  setSatelliteVisibility,
  toggleSatelliteVisibility,
  setImageryData,
  aoiTooLarge,
  setSearchLoading,
  clearCatalogResults,
  setCatalogSource,
} = catalogSlice.actions;
export default catalogSlice.reducer;
